import React, { useCallback } from 'react';
import { IonButton, IonIcon } from '@ionic/react';
import { copyOutline } from 'ionicons/icons';
import { useAppToast } from '@/hooks/useAppToast';
import { pxToRem } from '@/modules/config/constants';

interface ObfuscatedEmailProps {
  email: string;
}

const textPartStyle: React.CSSProperties = {
  // Anti-scrape: the visible address can't be selected, and pointer-events are
  // disabled so it isn't trivially click-dragged. The real address is only
  // reassembled in JS when the user deliberately taps Copy or Email.
  userSelect: 'none',
  WebkitUserSelect: 'none',
  pointerEvents: 'none',
  fontSize: pxToRem(16),
  color: 'var(--ion-color-secondary)',
  wordBreak: 'break-all',
} as React.CSSProperties;

/**
 * Renders an email address with light anti-scraping defenses:
 *
 *  - the address is split at the last `@` and rendered across separate spans
 *    (local / `@` / domain), so it never appears as a single contiguous text
 *    node a naive innerHTML/regex scrape could lift verbatim;
 *  - the visible text is non-selectable (`user-select: none`) with
 *    `pointer-events: none`;
 *  - there is no `mailto:` link anywhere — emailing a manager takes a deliberate
 *    Copy + paste, which keeps it from being a one-tap spam target.
 *
 * Accessibility is preserved: an `aria-label` carries the full address so screen
 * readers announce it despite the visual split, and the Copy action gives every
 * user a deliberate way to use the address.
 */
const ObfuscatedEmail: React.FC<ObfuscatedEmailProps> = ({ email }) => {
  const [present] = useAppToast();

  // Split on the LAST `@` so unusual-but-valid locals containing `@` survive.
  const atIndex = email.lastIndexOf('@');
  const local = atIndex >= 0 ? email.slice(0, atIndex) : email;
  const domain = atIndex >= 0 ? email.slice(atIndex + 1) : '';

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(email);
      await present({
        message: 'Email address copied',
        duration: 2000,
        color: 'success',
      });
    } catch {
      await present({
        message: 'Could not copy email address',
        duration: 2000,
        color: 'danger',
      });
    }
  }, [email, present]);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '8px',
        width: '100%',
      }}
    >
      <span
        data-testid="manager-email"
        aria-label={email}
        style={{ minWidth: 0 }}
      >
        <span style={textPartStyle}>{local}</span>
        <span style={textPartStyle} aria-hidden="true">
          @
        </span>
        <span style={textPartStyle}>{domain}</span>
      </span>
      <span style={{ flex: '0 0 auto', whiteSpace: 'nowrap' }}>
        <IonButton
          fill="clear"
          size="small"
          data-testid="manager-email-copy"
          aria-label={`Copy email address ${email}`}
          onClick={handleCopy}
        >
          <IonIcon icon={copyOutline} slot="icon-only" />
        </IonButton>
      </span>
    </div>
  );
};

export default ObfuscatedEmail;
